import React, { useMemo, useState, useEffect, useRef, ReactNode } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { paginationTotalRenderer, columnsFactory, StyleContainer } from './InteractiveTable';
import { CenteredAlert } from 'components/styled';
import { useTranslation } from 'react-i18next';
import { SortOrder } from 'constants/literals';
import ReactLoadingOverlay from 'react-loading-overlay';

type TableChangeEventType = 'filter' | 'pagination' | 'sort' | 'cellEdit';

interface TableState {
    page: number;
    sizePerPage: number;
    sortField: string;
    sortOrder: SortOrder;
}

interface Props {
    keyField: string;
    data: object[];
    columns: object[];
    paginationOptions: {
        page: number;
        sizePerPage: number;
        totalSize: number;
        hideSizePerPage?: boolean;
        showTotal?: boolean;
    };
    defaultSorted: {
        dataField: string;
        order: SortOrder;
    };
    loading: boolean;
    noPadding?: boolean;
    fetchPage: (pageNumber: number, pageSize: number, sortField: string, sortOrder: SortOrder) => void;
}

const defaultPaginationOptions = {
    hideSizePerPage: true,
    showTotal: true,
    alwaysShowAllBtns: true,
    withFirstAndLast: false,
    paginationTotalRenderer,
    sizePerPageList: [
        { text: '10', value: 10 },
        { text: '25', value: 25 },
        { text: '50', value: 50 },
        { text: '100', value: 100 },
    ],
};

function useDefferedValues(loading: boolean, ...values: any[]) {
    const [defferedValues, setDefferedValues] = useState(values);

    useEffect(() => {
        if (!loading) {
            setDefferedValues(values);
        }
    }, [...values, loading, setDefferedValues]);

    return defferedValues;
}

function parsePixels(value: string) {
    return parseFloat(value.replace('px', ''));
}

interface LoadingOverlayProps {
    children: ReactNode;
}

function LoadingOverlay(props: LoadingOverlayProps) {
    const overlayRef = useRef<any>(null);

    useEffect(() => {
        const wrapper = overlayRef.current!.wrapper.current;
        const masker = wrapper.firstChild;
        const header = wrapper.parentElement.querySelector('thead');
        const body = wrapper.parentElement.querySelector('tbody');
        const caption = wrapper.parentElement.querySelector('caption');

        let marginTop = parsePixels(window.getComputedStyle(header).height!);
        if (caption) {
            marginTop += parsePixels(window.getComputedStyle(caption).height!);
        }

        masker.style.marginTop = `${marginTop}px`;
        masker.style.height = window.getComputedStyle(body).height;
    }, []);

    return (
        <ReactLoadingOverlay
            ref={(r) => (overlayRef.current = r)}
            styles={{
                overlay: (base) => ({ ...base, background: 'rgba(255, 255, 255, 0.8)' }),
            }}
            active={true}
        >
            {props.children}
        </ReactLoadingOverlay>
    );
}

function NoOverlay(props: LoadingOverlayProps) {
    return props.children;
}

function InteractiveRemoteTable(props: Props) {
    const { keyField, data, columns, paginationOptions, defaultSorted, loading, noPadding = false, fetchPage } = props;
    const { t } = useTranslation();
    const processedColumns = useMemo(() => columnsFactory(columns), [columns]);
    const [defferedData, defferedPaginationOptions] = useDefferedValues(loading, data, paginationOptions);
    const pagination = useMemo(() => {
        const options = {
            ...defaultPaginationOptions,
            ...defferedPaginationOptions,
            currPage: defferedPaginationOptions.page,
            currSizePerPage: defferedPaginationOptions.sizePerPage,
        };
        if (defferedData.length === 0) {
            options.showTotal = false;
        }
        return paginationFactory(options);
    }, [defferedPaginationOptions, defferedData]);

    function onTableChange(eventType: TableChangeEventType, newState: TableState) {
        if (eventType === 'pagination') {
            fetchPage(newState.page, newState.sizePerPage, newState.sortField, newState.sortOrder);
        } else if (eventType === 'sort') {
            fetchPage(1, newState.sizePerPage, newState.sortField, newState.sortOrder);
        }
    }

    return (
        <StyleContainer addPadding={!noPadding}>
            <BootstrapTable
                remote
                bootstrap4
                bordered={false}
                hover
                keyField={keyField}
                data={defferedData}
                columns={processedColumns}
                onTableChange={onTableChange}
                pagination={pagination}
                defaultSorted={[defaultSorted]}
                loading={loading}
                overlay={(loading: boolean) => (loading ? LoadingOverlay : NoOverlay)}
                noDataIndication={() => (
                    <CenteredAlert color="none">{loading ? t('common.loading') : t('common.noData')}</CenteredAlert>
                )}
            />
        </StyleContainer>
    );
}

export default InteractiveRemoteTable;
